import { Component } from '@theme/component';
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

/*
 * LumvinWalls product page helpers.
 *
 * - <lumvin-wall-fit-component>: "Find your size" — recommends a Dimensions value for a wall and
 *   selects it through the native variant picker.
 * - <lumvin-sample-order-component>: adds the Sample variant for the selected Material to the cart
 *   through the theme's cart events, so the cart bubble and drawer update like a normal add.
 *
 * Both are progressive: the variant picker, buy buttons and the plain sample form keep working
 * without this module. Pure helpers are exported for unit tests.
 */

const CM_PER_INCH = 2.54;
const MAX_WALL_INCHES = 2000;

/* ------------------------------------------------------------------ */
/* Pure helpers                                                        */
/* ------------------------------------------------------------------ */

const NUMBER = '(\\d+(?:[.,]\\d+)?)';
const UNIT = '(cm|mm|in(?:ch(?:es)?)?\\b|")?';
const SIDE = '(w(?:idth|ide)?|h(?:eight|igh)?)?';
const DIMENSION_PATTERN = new RegExp(
  `${NUMBER}\\s*${UNIT}\\s*${SIDE}\\s*(?:x|by)\\s*${NUMBER}\\s*${UNIT}\\s*${SIDE}(?:\\s*(cm|mm|in(?:ch(?:es)?)?\\b|"))?`
);

/**
 * Normalises curly quotes, prime marks and the multiplication sign so one pattern can read them.
 * @param {string} label
 * @returns {string}
 */
function normaliseLabel(label) {
  return String(label)
    .toLowerCase()
    .replace(/[“”„‟″‶ʺ˝＂]/g, '"')
    .replace(/[‘’′‵]{2}|''/g, '"')
    .replace(/[×✕✖⨯]/g, 'x');
}

/**
 * @param {string | undefined} unit
 * @returns {number} Multiplier that converts the unit to inches.
 */
function unitToInches(unit) {
  if (!unit) return 1;
  if (unit === 'cm') return 1 / CM_PER_INCH;
  if (unit === 'mm') return 1 / (CM_PER_INCH * 10);
  return 1;
}

/**
 * @typedef {object} ParsedDimension
 * @property {string} label - The original option value.
 * @property {number} width - Width in inches.
 * @property {number} height - Height in inches.
 * @property {string} tag - Text left over around the size (e.g. a panel name), lowercased.
 */

/**
 * Reads a Dimensions option value such as `60" W x 40" H`, `60"W × 40"H`, `110”Wx102”H (Angela)`,
 * `287 cm W x 245 cm H` or `Vincenzo(70"Wx 96"H)`. Returns sizes in inches, or null when the
 * value is not a size (e.g. `Sample`).
 * @param {string} label
 * @returns {ParsedDimension | null}
 */
export function parseDimensionLabel(label) {
  if (typeof label !== 'string' || !label.trim()) return null;

  const text = normaliseLabel(label);
  const match = DIMENSION_PATTERN.exec(text);
  if (!match) return null;

  const [whole, firstValue, firstUnit, firstSide, secondValue, secondUnit, secondSide, trailingUnit] = match;
  const first = Number.parseFloat(String(firstValue).replace(',', '.'));
  const second = Number.parseFloat(String(secondValue).replace(',', '.'));
  if (!Number.isFinite(first) || !Number.isFinite(second) || first <= 0 || second <= 0) return null;

  // A unit written once (e.g. "60 x 40 cm") applies to both numbers.
  const sharedUnit = trailingUnit || secondUnit || firstUnit;
  let width = first * unitToInches(firstUnit || sharedUnit);
  let height = second * unitToInches(secondUnit || trailingUnit || firstUnit);

  // Values are written width first; swap when the letters say otherwise ("40" H x 60" W").
  if (firstSide?.startsWith('h') && (!secondSide || secondSide.startsWith('w'))) {
    [width, height] = [height, width];
  }

  const tag = text
    .replace(whole, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

  return { label, width, height, tag };
}

/**
 * Builds the list of standard sizes from the Dimensions option values. Sample (and anything that
 * is not a size) is ignored. Returns an empty list unless at least two sizes describe the same
 * design: values carrying different panel names (e.g. "(Kaoru)" and "(Kaoru Center)") are separate
 * pieces rather than a size ladder, so no recommendation is made for them.
 * @param {string[]} labels
 * @param {string} [sampleValue]
 * @returns {ParsedDimension[]} Sizes sorted by area, smallest first.
 */
export function parseSizeLadder(labels, sampleValue = 'Sample') {
  if (!Array.isArray(labels)) return [];

  const sample = String(sampleValue || '').trim().toLowerCase();
  /** @type {ParsedDimension[]} */
  const sizes = [];
  const seen = new Set();

  for (const label of labels) {
    if (typeof label !== 'string') continue;
    if (sample && label.trim().toLowerCase() === sample) continue;
    if (seen.has(label)) continue;

    const size = parseDimensionLabel(label);
    if (!size) continue;

    seen.add(label);
    sizes.push(size);
  }

  const tags = new Set(sizes.map((size) => size.tag));
  if (sizes.length < 2 || tags.size > 1) return [];

  return sizes.sort((a, b) => a.width * a.height - b.width * b.height || a.width - b.width);
}

/**
 * @typedef {object} SizeRecommendation
 * @property {'fits' | 'exact' | 'trim' | 'none'} status
 *   fits: a size covers the wall with room to trim; exact: a size matches the wall;
 *   trim: the wall is smaller than every size, so the smallest is recommended;
 *   none: no standard size covers the wall.
 * @property {ParsedDimension | null} size - The recommended size (largest size when status is none).
 */

/**
 * Recommends the smallest size (by area) whose width and height both cover the wall.
 * @param {ParsedDimension[]} sizes - From parseSizeLadder.
 * @param {number} wallWidth - Inches.
 * @param {number} wallHeight - Inches.
 * @returns {SizeRecommendation | null} Null when the inputs are not usable.
 */
export function recommendSize(sizes, wallWidth, wallHeight) {
  if (!Array.isArray(sizes) || sizes.length === 0) return null;
  if (!(wallWidth > 0) || !(wallHeight > 0)) return null;

  // Ignore floating point noise from cm conversions (1/100 inch).
  const tolerance = 0.01;
  const candidates = sizes
    .filter((size) => size.width + tolerance >= wallWidth && size.height + tolerance >= wallHeight)
    .sort((a, b) => a.width * a.height - b.width * b.height || a.width - b.width);

  const best = candidates[0];

  if (!best) {
    const largest = sizes.reduce((a, b) => (b.width * b.height > a.width * a.height ? b : a));
    return { status: 'none', size: largest };
  }

  const exact = Math.abs(best.width - wallWidth) < tolerance && Math.abs(best.height - wallHeight) < tolerance;
  if (exact) return { status: 'exact', size: best };

  const smallest = sizes.reduce((a, b) => (b.width * b.height < a.width * a.height ? b : a));
  if (best === smallest && wallWidth < smallest.width && wallHeight < smallest.height) {
    return { status: 'trim', size: best };
  }

  return { status: 'fits', size: best };
}

/**
 * @param {number} value
 * @param {'in' | 'cm'} unit
 * @returns {number} Inches.
 */
export function toInches(value, unit) {
  return unit === 'cm' ? value / CM_PER_INCH : value;
}

/**
 * @param {string} raw - Input value; accepts a decimal comma.
 * @returns {number} NaN when empty or invalid.
 */
export function parseMeasurement(raw) {
  const text = String(raw ?? '').trim().replace(',', '.');
  if (!text) return Number.NaN;
  const value = Number(text);
  return Number.isFinite(value) ? value : Number.NaN;
}

/* ------------------------------------------------------------------ */
/* Native variant picker access                                        */
/* ------------------------------------------------------------------ */

/**
 * @param {string | undefined} value
 * @returns {string}
 */
function normaliseValue(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase();
}

/**
 * Finds the main product variant picker for a block.
 * @param {Element} element
 * @returns {HTMLElement | null}
 */
export function findMainPicker(element) {
  const scope = element.closest('product-component, .shopify-section') ?? document;
  return (
    scope.querySelector('variant-picker[data-template-product-match="true"]') ?? scope.querySelector('variant-picker')
  );
}

/**
 * Lists the controls (radio inputs or select options) for one product option.
 * @param {Element} picker
 * @param {string} optionName
 * @returns {Array<HTMLInputElement | HTMLOptionElement>}
 */
function getOptionControls(picker, optionName) {
  const name = normaliseValue(optionName);
  /** @type {Array<HTMLInputElement | HTMLOptionElement>} */
  const controls = [];

  for (const control of picker.querySelectorAll('input[type="radio"][data-option-name], select option[data-option-name]')) {
    if (!(control instanceof HTMLInputElement || control instanceof HTMLOptionElement)) continue;
    if (normaliseValue(control.dataset.optionName) === name) controls.push(control);
  }

  return controls;
}

/**
 * Reads the selected value of an option from the native picker.
 * @param {Element | null} picker
 * @param {string} optionName
 * @returns {string | null}
 */
export function getSelectedOptionValue(picker, optionName) {
  if (!picker) return null;

  for (const control of getOptionControls(picker, optionName)) {
    if (control instanceof HTMLInputElement && control.checked) return control.value;
    if (control instanceof HTMLOptionElement && control.selected) return control.value;
  }

  return null;
}

/**
 * Selects an option value through the same path as a shopper: a click on the radio (which fires
 * `change` on the variant picker) or a `change` event on the select.
 * @param {Element | null} picker
 * @param {string} optionName
 * @param {string} value
 * @returns {boolean} True when the value exists in the picker.
 */
export function selectOptionValue(picker, optionName, value) {
  if (!picker) return false;

  const target = normaliseValue(value);
  const control = getOptionControls(picker, optionName).find((item) => normaliseValue(item.value) === target);
  if (!control) return false;

  if (control instanceof HTMLInputElement) {
    if (!control.checked) control.click();
    return true;
  }

  const select = control.closest('select');
  if (!select) return false;
  if (select.value !== control.value) {
    select.value = control.value;
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  return true;
}

/** @type {WeakSet<Element>} */
const sampleCorrectedPickers = new WeakSet();

/**
 * Sample is often the first Dimensions value, so the product page would open on the sample price.
 * Once per page, and only without an explicit `?variant=`, switch to the first real size for the
 * selected Material through the native picker.
 * @param {Element} element - A block inside the product section.
 * @param {string} dimensionOption
 * @param {string} sampleValue
 */
export async function ensureMuralSizeSelected(element, dimensionOption, sampleValue) {
  if (!dimensionOption || !sampleValue) return;
  if (new URL(window.location.href).searchParams.has('variant')) return;

  await customElements.whenDefined('variant-picker');

  const picker = findMainPicker(element);
  if (!picker || sampleCorrectedPickers.has(picker)) return;
  sampleCorrectedPickers.add(picker);

  const sample = normaliseValue(sampleValue);
  if (normaliseValue(getSelectedOptionValue(picker, dimensionOption) ?? '') !== sample) return;

  const sizes = getOptionControls(picker, dimensionOption).filter((control) => normaliseValue(control.value) !== sample);
  const firstAvailable =
    sizes.find((control) => control.dataset.optionAvailable !== 'false' && !control.disabled) ?? sizes[0];

  if (firstAvailable) selectOptionValue(picker, dimensionOption, firstAvailable.value);
}

/**
 * @param {Element} element
 * @returns {Record<string, any>}
 */
function readJson(element) {
  try {
    return JSON.parse(element?.textContent || 'null') ?? {};
  } catch {
    return {};
  }
}

/**
 * Fills an element from a translated template, wrapping `[size]` in <strong>.
 * @param {HTMLElement} element
 * @param {string} template
 * @param {Record<string, string>} values
 */
function renderTemplate(element, template, values) {
  element.replaceChildren();

  for (const part of String(template).split(/(\[[a-z_]+\])/)) {
    const key = part.match(/^\[([a-z_]+)\]$/)?.[1];

    if (key && key in values) {
      if (key === 'size') {
        const strong = document.createElement('strong');
        strong.textContent = values[key] ?? '';
        element.append(strong);
      } else {
        element.append(values[key] ?? '');
      }
    } else if (part) {
      element.append(part);
    }
  }
}

/**
 * @param {string} template
 * @param {Record<string, string>} values
 * @returns {string}
 */
function fillTemplate(template, values) {
  return String(template).replace(/\[([a-z_]+)\]/g, (match, key) => (key in values ? values[key] ?? '' : match));
}

/* ------------------------------------------------------------------ */
/* <lumvin-wall-fit-component>                                         */
/* ------------------------------------------------------------------ */

/**
 * @typedef {object} WallFitRefs
 * @property {HTMLScriptElement} sizesData
 * @property {HTMLScriptElement} strings
 * @property {HTMLInputElement} widthInput
 * @property {HTMLInputElement} heightInput
 * @property {HTMLElement[]} [unitSuffixes]
 * @property {HTMLElement} error
 * @property {HTMLElement} result
 * @property {HTMLElement} message
 * @property {HTMLElement} liveRegion
 * @property {HTMLElement} [preview]
 * @property {HTMLElement} [panelBox]
 * @property {HTMLElement} [wallBox]
 * @property {HTMLElement} [panelCaption]
 * @property {HTMLButtonElement} [selectButton]
 * @property {HTMLElement} [customLink]
 */

/**
 * "Find your size" for murals sold in a Dimensions option ladder.
 *
 * @extends {Component<WallFitRefs>}
 */
class LumvinWallFit extends Component {
  requiredRefs = ['sizesData', 'strings', 'widthInput', 'heightInput', 'error', 'result', 'message', 'liveRegion'];

  /** @type {ParsedDimension[]} */
  #sizes = [];

  /** @type {Record<string, string>} */
  #strings = {};

  /** @type {'in' | 'cm'} */
  #unit = 'in';

  /** @type {ParsedDimension | null} */
  #recommended = null;

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.#strings = readJson(this.refs.strings);
    const labels = readJson(this.refs.sizesData);
    this.#sizes = parseSizeLadder(Array.isArray(labels) ? labels : [], this.dataset.sampleValue);

    // Fewer than two readable sizes: nothing to recommend.
    if (this.#sizes.length < 2) {
      this.hidden = true;
      return;
    }

    this.hidden = false;
    this.#unit = this.querySelector('input[name^="lumvin-wall-unit"]:checked')?.getAttribute('value') === 'cm' ? 'cm' : 'in';

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const section = this.closest('.shopify-section, dialog');
    section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, {
      signal: this.#abortController.signal,
    });

    ensureMuralSizeSelected(this, this.dataset.dimensionOption ?? '', this.dataset.sampleValue ?? '').catch(() => {});
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /**
   * Converts typed values when the unit changes.
   * @param {Event} event
   */
  handleUnitChange(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.checked) return;

    const nextUnit = input.value === 'cm' ? 'cm' : 'in';
    if (nextUnit === this.#unit) return;

    for (const field of [this.refs.widthInput, this.refs.heightInput]) {
      const value = parseMeasurement(field.value);
      if (!Number.isFinite(value)) continue;
      const converted = nextUnit === 'cm' ? value * CM_PER_INCH : value / CM_PER_INCH;
      field.value = String(Math.round(converted * 10) / 10);
    }

    this.#unit = nextUnit;
    for (const suffix of this.refs.unitSuffixes ?? []) {
      suffix.textContent = nextUnit === 'cm' ? this.#strings.unit_cm ?? 'cm' : this.#strings.unit_in ?? 'in';
    }

    if (this.#recommended || !this.refs.result.hidden) this.#calculate({ announce: false });
  }

  /**
   * @param {SubmitEvent} event
   */
  handleSubmit(event) {
    event.preventDefault();
    this.#calculate({ announce: true });
  }

  /**
   * Selects the recommended size in the native variant picker.
   */
  selectRecommended() {
    const size = this.#recommended;
    if (!size || this.refs.selectButton?.getAttribute('aria-disabled') === 'true') return;

    const picker = findMainPicker(this);
    const selected = selectOptionValue(picker, this.dataset.dimensionOption ?? '', size.label);

    if (selected) {
      this.refs.liveRegion.textContent = fillTemplate(this.#strings.selected_announcement ?? '', { size: size.label });
      this.#syncSelectButton();
    }
  }

  /**
   * @param {{ announce: boolean }} options
   */
  #calculate({ announce }) {
    const { widthInput, heightInput, error } = this.refs;
    const width = parseMeasurement(widthInput.value);
    const height = parseMeasurement(heightInput.value);

    const invalidWidth = !(width > 0) || toInches(width, this.#unit) > MAX_WALL_INCHES;
    const invalidHeight = !(height > 0) || toInches(height, this.#unit) > MAX_WALL_INCHES;

    widthInput.toggleAttribute('aria-invalid', invalidWidth);
    heightInput.toggleAttribute('aria-invalid', invalidHeight);

    if (invalidWidth || invalidHeight) {
      error.textContent = this.#strings.error ?? '';
      error.hidden = false;
      this.refs.result.hidden = true;
      this.#recommended = null;
      if (announce) {
        this.refs.liveRegion.textContent = error.textContent;
        (invalidWidth ? widthInput : heightInput).focus();
      }
      return;
    }

    error.hidden = true;
    error.textContent = '';

    const wallWidth = toInches(width, this.#unit);
    const wallHeight = toInches(height, this.#unit);
    const recommendation = recommendSize(this.#sizes, wallWidth, wallHeight);
    if (!recommendation?.size) return;

    const { status, size } = recommendation;
    const values = {
      size: this.#formatSize(size),
      wall: this.#formatWall(width, height),
    };

    const templates = {
      fits: this.#strings.result_fits,
      exact: this.#strings.result_exact,
      trim: this.#strings.result_trim,
      none: this.#strings.result_none,
    };

    renderTemplate(this.refs.message, templates[status] ?? '', values);
    this.refs.result.hidden = false;
    this.refs.result.dataset.status = status;

    const fits = status !== 'none';
    this.#recommended = fits ? size : null;

    if (this.refs.customLink) this.refs.customLink.hidden = fits;
    if (this.refs.preview) this.refs.preview.hidden = !fits;
    if (fits) this.#renderPreview(size, wallWidth, wallHeight, values);
    this.#syncSelectButton();

    if (announce) this.refs.liveRegion.textContent = this.refs.message.textContent ?? '';
  }

  /**
   * Draws the wall inside the recommended mural; the shaded margin is the excess to trim.
   * @param {ParsedDimension} size
   * @param {number} wallWidth
   * @param {number} wallHeight
   * @param {Record<string, string>} values
   */
  #renderPreview(size, wallWidth, wallHeight, values) {
    const { panelBox, wallBox, panelCaption } = this.refs;
    if (!panelBox || !wallBox) return;

    panelBox.style.setProperty('--lumvin-panel-ratio', `${size.width} / ${size.height}`);
    panelBox.style.setProperty('--lumvin-panel-ratio-number', String(size.width / size.height));
    wallBox.style.setProperty('--lumvin-wall-width', `${Math.min(100, (wallWidth / size.width) * 100)}%`);
    wallBox.style.setProperty('--lumvin-wall-height', `${Math.min(100, (wallHeight / size.height) * 100)}%`);
    panelBox.setAttribute('aria-label', fillTemplate(this.#strings.preview_label ?? '', values));
    if (panelCaption) panelCaption.textContent = values.size ?? '';
  }

  #syncSelectButton() {
    const { selectButton } = this.refs;
    if (!selectButton) return;

    const size = this.#recommended;
    selectButton.hidden = !size;
    if (!size) return;

    const current = getSelectedOptionValue(findMainPicker(this), this.dataset.dimensionOption ?? '');
    const isSelected = normaliseValue(current ?? '') === normaliseValue(size.label);

    selectButton.setAttribute('aria-disabled', String(isSelected));
    selectButton.dataset.selected = String(isSelected);
    const label = selectButton.querySelector('[data-label]');
    if (label) label.textContent = isSelected ? this.#strings.selected ?? '' : this.#strings.select ?? '';
  }

  #handleProductSelect = () => {
    // The picker updates its checked state before dispatching, so the selection is current here.
    if (this.#recommended) this.#syncSelectButton();
  };

  /**
   * @param {ParsedDimension} size
   * @returns {string}
   */
  #formatSize(size) {
    if (this.#unit !== 'cm') return size.label;
    const cm = fillTemplate(this.#strings.wall_cm ?? '[width] × [height] cm', {
      width: this.#formatNumber(size.width * CM_PER_INCH, 0),
      height: this.#formatNumber(size.height * CM_PER_INCH, 0),
    });
    return `${size.label} (${cm})`;
  }

  /**
   * @param {number} width - In the current unit.
   * @param {number} height - In the current unit.
   * @returns {string}
   */
  #formatWall(width, height) {
    const template = this.#unit === 'cm' ? this.#strings.wall_cm : this.#strings.wall_in;
    return fillTemplate(template ?? '[width] × [height]', {
      width: this.#formatNumber(width, 1),
      height: this.#formatNumber(height, 1),
    });
  }

  /**
   * @param {number} value
   * @param {number} digits
   * @returns {string}
   */
  #formatNumber(value, digits) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        maximumFractionDigits: digits,
      }).format(value);
    } catch {
      return String(Math.round(value * 10 ** digits) / 10 ** digits);
    }
  }
}

if (!customElements.get('lumvin-wall-fit-component')) {
  customElements.define('lumvin-wall-fit-component', LumvinWallFit);
}

/* ------------------------------------------------------------------ */
/* <lumvin-sample-order-component>                                     */
/* ------------------------------------------------------------------ */

/**
 * @typedef {object} SampleVariant
 * @property {number | string} id
 * @property {string} material
 * @property {string} price
 * @property {boolean} available
 */

/**
 * @typedef {object} SampleOrderRefs
 * @property {HTMLScriptElement} samplesData
 * @property {HTMLScriptElement} strings
 * @property {HTMLFormElement} form
 * @property {HTMLInputElement} variantInput
 * @property {HTMLButtonElement} button
 * @property {HTMLElement} price
 * @property {HTMLElement} [material]
 * @property {HTMLElement} status
 */

/**
 * "Order a sample" — adds the Sample variant for the selected Material.
 *
 * @extends {Component<SampleOrderRefs>}
 */
class LumvinSampleOrder extends Component {
  requiredRefs = ['samplesData', 'strings', 'form', 'variantInput', 'button', 'price', 'status'];

  /** @type {SampleVariant[]} */
  #samples = [];

  /** @type {Record<string, string>} */
  #strings = {};

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {AbortController | undefined} */
  #requestController;

  /** @type {number | undefined} */
  #statusTimeout;

  connectedCallback() {
    super.connectedCallback();

    const samples = readJson(this.refs.samplesData);
    this.#samples = Array.isArray(samples) ? samples : [];
    this.#strings = readJson(this.refs.strings);

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const section = this.closest('.shopify-section, dialog');
    section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, {
      signal: this.#abortController.signal,
    });

    ensureMuralSizeSelected(this, this.dataset.dimensionOption ?? '', this.dataset.sampleValue ?? '').catch(() => {});
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#requestController?.abort();
    clearTimeout(this.#statusTimeout);
  }

  /**
   * @param {Event & { selectedOptions?: Array<{ name: string, value: string }> }} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const materialOption = normaliseValue(this.dataset.materialOption);
    const fromEvent = Array.isArray(event.selectedOptions)
      ? event.selectedOptions.find((option) => normaliseValue(option?.name) === materialOption)?.value
      : undefined;
    const material = fromEvent ?? getSelectedOptionValue(findMainPicker(this), this.dataset.materialOption ?? '');

    this.#showSampleFor(material);
  };

  /**
   * @param {string | null | undefined} material
   */
  #showSampleFor(material) {
    if (!this.#samples.length) return;

    const target = normaliseValue(material ?? '');
    const sample =
      this.#samples.find((item) => normaliseValue(item.material) === target && item.available) ??
      this.#samples.find((item) => normaliseValue(item.material) === target) ??
      this.#samples.find((item) => item.available) ??
      this.#samples[0];
    if (!sample) return;

    this.refs.variantInput.value = String(sample.id);
    this.refs.price.textContent = sample.price;
    if (this.refs.material) this.refs.material.textContent = sample.material;
    this.refs.button.toggleAttribute('disabled', !sample.available);
    this.dataset.material = sample.material;
  }

  /**
   * Adds the sample through the cart AJAX API and dispatches the theme's cart event so the cart
   * bubble, cart items and drawer refresh exactly as after a normal add.
   * @param {SubmitEvent} event
   */
  async handleSubmit(event) {
    event.preventDefault();

    const { button, variantInput } = this.refs;
    const variantId = variantInput.value;
    if (!variantId || button.disabled || button.getAttribute('aria-busy') === 'true') return;

    this.#requestController?.abort();
    this.#requestController = new AbortController();
    const { signal } = this.#requestController;

    button.setAttribute('aria-busy', 'true');
    this.#setStatus('', 'idle');

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    const cartRoutes = /** @type {any} */ (window).Theme?.routes ?? {};
    const addUrl = cartRoutes.cart_add_url || this.refs.form.getAttribute('action') || '/cart/add';

    try {
      const response = await fetch(addUrl.endsWith('.js') ? addUrl : `${addUrl}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          ...(sectionIds.length ? { sections: sectionIds.join(',') } : {}),
        }),
        signal,
      });
      const body = await response.json();

      if (!response.ok || body.status) {
        const message = body.description || body.message || this.#strings.error || '';
        this.dispatchEvent(
          new CartErrorEvent({
            error: body.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: body.description, errors: body.errors },
          })
        );

        this.#fetchCart(cartRoutes.cart_url)
          .then((cart) =>
            deferred.resolve({
              cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
              detail: { didError: true, items: cart.items, source: 'lumvin-sample-order', sourceId: this.id, itemCount: 1 },
            })
          )
          .catch(deferred.reject);

        this.#setStatus(message, 'error');
        return;
      }

      const cart = await this.#fetchCart(cartRoutes.cart_url);
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'lumvin-sample-order',
          sourceId: this.id,
          itemCount: 1,
          productId: this.dataset.productId,
          sections: body.sections,
          didError: false,
        },
      });

      this.#setStatus(fillTemplate(this.#strings.added ?? '', { material: this.dataset.material ?? '' }), 'success');
    } catch (error) {
      deferred.reject(error);
      if (/** @type {Error} */ (error)?.name === 'AbortError') return;

      this.dispatchEvent(
        new CartErrorEvent({
          error: /** @type {Error} */ (error)?.message || 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      this.#setStatus(this.#strings.error ?? '', 'error');
    } finally {
      button.removeAttribute('aria-busy');
    }
  }

  /**
   * @param {string | undefined} cartUrl
   * @returns {Promise<any>}
   */
  async #fetchCart(cartUrl) {
    const cartItems = /** @type {any} */ (document.querySelector('cart-items-component'));
    if (cartItems) {
      await customElements.whenDefined('cart-items-component');
      if (typeof cartItems.fetchCartData === 'function') return cartItems.fetchCartData();
    }

    const response = await fetch(`${cartUrl || '/cart'}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  }

  /**
   * @param {string} message
   * @param {'idle' | 'success' | 'error'} state
   */
  #setStatus(message, state) {
    const { status } = this.refs;
    clearTimeout(this.#statusTimeout);
    status.textContent = message;
    status.dataset.state = state;

    if (state === 'success') {
      this.#statusTimeout = window.setTimeout(() => {
        status.textContent = '';
        status.dataset.state = 'idle';
      }, 6000);
    }
  }
}

if (!customElements.get('lumvin-sample-order-component')) {
  customElements.define('lumvin-sample-order-component', LumvinSampleOrder);
}
